import React from "react"
import { TrashIcon, MinusIcon, PlusIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import useT from "../../hooks/useT"
import { isOutOfStock, overOrderLimit } from "../../utils/productUtils"
import Value from "aldoo-ra/Value"
import { updateCartLine, removeFromCart, useCart, cartQuantityOf, cartLineOf } from "../../store/cart"
import { CART_VALIDATION_KEY, lineCheckOf } from "../../store/stockCheck"
import { lineKey } from "../../checkout/cartItems"
import useSingleCopy from "../../hooks/useSingleCopy"

/**
 * How many of a product are in the cart, with one more / one fewer: a pill
 * with a bin (at one) or minus, the count, and plus — the shortcut the big
 * shops put on cards and in their cart rail. Renders nothing while the
 * product is not in the cart. Given a `line`, it steps that line (a size of
 * the product, say); otherwise the product's plain line. What is had once (a
 * download, access to content) has no count: the pill only takes it out.
 */
export default function CartStepper({ product, line: given = null, className = "", placement = "other" }) {
  const t = useT()
  const cart = useCart()
  const single = useSingleCopy(product)
  // the server's last word on this line (store/stockCheck): how many it may hold
  const [validation] = Value(CART_VALIDATION_KEY, null)
  const line = given ? cart.find((entry) => lineKey(entry) === lineKey(given)) || null : cartLineOf(cart, product?.pid)
  const inCart = line?.quantity || 0
  if (!product?.pid || inCart <= 0) return null
  // the stock is the product's, whichever lines hold it
  // and as many as one order may hold (the seller's limit), whichever lines hold it
  const cap = lineCheckOf(validation, line)?.cap
  // the server's cap when it has answered for this line (live stock, other
  // checkouts' holds, the seller's limit); until then what the card loaded with
  const outOfStock = Number.isFinite(cap) ? inCart >= cap : isOutOfStock(product, cartQuantityOf(cart, product.pid)) || overOrderLimit(product, cartQuantityOf(cart, product.pid))
  const key = lineKey(line)
  const options = line.options
  const stop = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  // in the cart or not: one button takes it out
  if (single) {
    return (
      <button
        type="button"
        className={twMerge("flex items-center justify-center gap-2 w-full h-[36px] rounded border-2 border-primary bg-white text-text text-[14px] font-bold hover:text-error hover:border-error", className)}
        onClick={(e) => { stop(e); removeFromCart(key) }}
        aria-label={t`Remove`}
        data-testid="cart-remove"
      >
        <TrashIcon className="w-5 h-5 shrink-0" />
        <span className="truncate">{t`Remove`}</span>
      </button>
    )
  }
  return (
    <div className={twMerge("flex items-center justify-between w-full h-[36px] rounded border-2 border-primary bg-white text-text", className)} data-testid="cart-stepper" onClick={stop}>
      <button
        type="button"
        className="flex items-center justify-center w-10 h-full text-text hover:text-primary"
        onClick={(e) => { stop(e); inCart <= 1 ? removeFromCart(key) : updateCartLine(key, inCart - 1, options) }}
        aria-label={inCart === 1 ? t`Remove` : t`Less`}
      >
        {inCart === 1 ? <TrashIcon className="w-5 h-5" /> : <MinusIcon className="w-5 h-5" />}
      </button>
      <span className="flex-1 text-center text-[15px] font-bold" data-testid="cart-stepper-count">{inCart}</span>
      <button
        type="button"
        className="flex items-center justify-center w-10 h-full text-text hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed"
        onClick={(e) => { stop(e); if (!outOfStock) updateCartLine(key, inCart + 1, options, placement) }}
        disabled={outOfStock}
        aria-label={t`More`}
      >
        <PlusIcon className="w-5 h-5" />
      </button>
    </div>
  )
}
